import { Fragment, h } from 'preact'
import { useEffect } from 'preact/hooks'
import { Suspense, lazy } from 'preact/compat'
import { useStateValue } from '../../state-management/store'
import { changeStep, changeLocation } from '../../state-management/actions'
import { Spinner } from '../spinner'
import { BookingStep } from '../../models/enums'
import { StepBody } from '../step-body'
import { SelfSchedulerFooter } from '../self-scheduler-footer'
import LocationTile from './location-tile'
import { ErrorMessage } from '../error-message'
import { Location } from '../../models/location'
import { softGreen } from '../../shared/colors'
import * as style from './style.css'

const MapSelector = lazy(() => import('./map-selector/map-selector'))

const Loading = () => (
    <div className={style.spinnerContainer}>
        <Spinner color={softGreen} />
    </div>
)

const LocationsSelector: preact.FunctionalComponent = () => {
    const [{ locations, selectedLocationId, loader, errorMessage, customization }, dispatch] = useStateValue()
    const enabledMaps = customization?.scheduling_additional_features_and_fields?.enabled_maps ?? false

    useEffect(() => {
        const skipLocationsScreen = locations.length === 1 && locations[0].onlineSchedulingEnabled
        const bookingStep = !skipLocationsScreen ? BookingStep.LOCATION : BookingStep.PATIENT_TYPE

        if (skipLocationsScreen) {
            changeLocation(dispatch, locations[0])
        }

        changeStep(dispatch, bookingStep)
    }, [dispatch, locations])

    const handleSetSelectedLocation = (location: Location) => () => {
        changeLocation(dispatch, location)
    }

    return (
        <section class={style.locationsSelectorContainer}>
            <StepBody>
                <div class={!loader ? style.locationsSelectorWrapper : style.locationsSelectorWrapperLoading}>
                    {loader ? (
                        <div class={style.locationsSelectorWrapperLoading}>
                            <Loading />
                        </div>
                    ) : Boolean(errorMessage) ? (
                        <ErrorMessage>{errorMessage}</ErrorMessage>
                    ) : (
                        <section>
                            {enabledMaps ? (
                                <Suspense fallback={<Loading />}>
                                    <MapSelector locations={locations} selectedLocationId={selectedLocationId} />
                                </Suspense>
                            ) : (
                                <Fragment>
                                    <div class={style.locationsSelectorHeader}>
                                        Please select the location you’d like to visit:
                                    </div>
                                    <div class={style.locationsSelector}>
                                        {locations?.map((location) => {
                                            return (
                                                <LocationTile
                                                    key={location.id}
                                                    location={location}
                                                    isSelected={location.id === selectedLocationId}
                                                    onClick={handleSetSelectedLocation(location)}
                                                />
                                            )
                                        })}
                                    </div>
                                </Fragment>
                            )}
                        </section>
                    )}
                </div>
            </StepBody>
            <SelfSchedulerFooter nextStep={BookingStep.PATIENT_TYPE} isNextDisabled={!selectedLocationId} />
        </section>
    )
}

export default LocationsSelector
